import React from 'react';
// @ts-expect-error - No type declarations available for this component
import StyledDropdown from '../../dropdowns/StyledDropdown';
import MultiSelectDropdownNew from '../../dropdowns/MultiSelectDropdownNew';
import { Draggable } from '@hello-pangea/dnd';
import { Label } from '../../../components/ui/label';
import { FormInput } from '../../../components/ui/form-input';
import { Switch } from '../../../../components/ui/switch';

import { InputType, QuestionType } from './CustomInputsTab_Events';

const INPUT_TYPE_MAPPING = {
  SHORT_TEXT_INPUT: 'Short Text Input',
  LONG_TEXT_INPUT: 'Long Text Input',
  SINGLE_SELECTION_DROPDOWN: 'Single Selection Dropdown',
  MULTI_SELECTION_DROPDOWN: 'Multi Selection Dropdown',
};

interface CustomInput {
  id: string;
  title: string;
  type: InputType | string;
  options: string[];
  required: boolean;
  questionType: QuestionType;
}

interface CustomInputsTabContentEventsProps {
  customInput: CustomInput;
  id: string;
  index: number;
  handleTitleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleTypeChange: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  handleOptionsChange: (options: string[], index: number) => void;
  handleRequiredChange: (value: boolean, index: number) => void;
  handleQuestionTypeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  removeCustomInput: (index: number) => void;
  handleFieldFocus: (fieldName: string) => void;
  isFieldInvalid: (fieldName: string) => boolean;
}

function CustomInputsTabContent_Events(props: CustomInputsTabContentEventsProps) {
  const {
    customInput,
    id,
    index,
    handleTitleChange,
    handleTypeChange,
    handleOptionsChange,
    handleRequiredChange,
    handleQuestionTypeChange,
    removeCustomInput,
    handleFieldFocus,
    isFieldInvalid,
  } = props;

  return (
    <Draggable draggableId={id} index={index}>
      {(provided) => (
        <div
          {...provided.draggableProps}
          ref={provided.innerRef}
          className="space-y-2 rounded-md border border-edge bg-paper-inset p-2"
        >
          {/* Question Field with drag/delete icons */}
          <div className="space-y-1">
            <div className="flex items-start justify-between">
              <Label className="text-base font-medium text-ink-secondary">Question</Label>
              <div className="flex items-center gap-1">
                <div {...provided.dragHandleProps} className="cursor-move">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M2 4H14M2 8H14M2 12H14"
                      stroke="#3A3429"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <button
                  onClick={() => removeCustomInput(index)}
                  className="pr-0 text-ink-muted transition-all duration-300 hover:text-ink-secondary"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 6L6 18" />
                    <path d="M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <FormInput
              id={`question-${index}`}
              data-type="title"
              data-index={index}
              value={customInput.title}
              onChange={handleTitleChange}
              onFocus={() => handleFieldFocus(`custom_fields-customInput-title-${index}`)}
              placeholder="e.g. How did you find our website?"
              className={`h-7 ${
                isFieldInvalid(`custom_fields-customInput-title-${index}`) ? 'border-red-500' : ''
              }`}
            />
          </div>

          {/* Input Type Field */}
          <div className="space-y-1">
            <Label className="text-base font-medium text-ink-secondary">Input Type</Label>
            <div
              className={`${
                isFieldInvalid(`custom_fields-customInput-type-${index}`)
                  ? 'rounded-md ring-1 ring-red-500'
                  : ''
              }`}
              onFocus={() => handleFieldFocus(`custom_fields-customInput-type-${index}`)}
            >
              <StyledDropdown
                dataIndex={index}
                values={Object.values(INPUT_TYPE_MAPPING)}
                selectedValue={
                  customInput.type === 'Select an Input Type'
                    ? customInput.type
                    : INPUT_TYPE_MAPPING[customInput.type as keyof typeof INPUT_TYPE_MAPPING]
                }
                setSelectedValue={handleTypeChange}
                width={'w-full'}
              />
            </div>
          </div>

          {/* Options Field (for dropdown types) */}
          {(customInput.type === 'SINGLE_SELECTION_DROPDOWN' ||
            customInput.type === 'MULTI_SELECTION_DROPDOWN') && (
            <div className="space-y-1">
              <Label className="text-base font-medium text-ink-secondary">Options</Label>
              <div onFocus={() => handleFieldFocus(`custom_fields-customInput-options-${index}`)}>
                <MultiSelectDropdownNew
                  height={28}
                  options={[]}
                  items={customInput.options || []}
                  setItems={(options) => handleOptionsChange(options, index)}
                  error={isFieldInvalid?.(`custom_fields-customInput-options-${index}`)}
                />
              </div>
            </div>
          )}

          {/* Required Toggle */}
          <div className="flex items-center gap-1">
            <Switch
              checked={customInput.required}
              onCheckedChange={(checked: boolean) => handleRequiredChange(checked, index)}
            />
            <Label className="text-base font-medium text-ink-secondary">Required</Label>
          </div>

          {/* Question Type */}
          <div className="space-y-1">
            <Label className="text-base font-medium text-ink-secondary">Question Type</Label>
            <div className="flex space-x-6">
              <button
                type="button"
                className="flex items-center gap-1"
                onClick={() => handleQuestionTypeChange({ target: { value: 'PURCHASER', dataset: { index } } } as any)}
              >
                <span
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 bg-white ${
                    customInput.questionType === 'PURCHASER' ? 'border-sage' : 'border-edge'
                  }`}
                  aria-hidden
                >
                  {customInput.questionType === 'PURCHASER' && (
                    <span className="h-2 w-2 rounded-full bg-sage" />
                  )}
                </span>
                <span className="text-sm text-ink-secondary">Purchaser</span>
              </button>
              <button
                type="button"
                className="flex items-center gap-1"
                onClick={() => handleQuestionTypeChange({ target: { value: 'ATTENDEE', dataset: { index } } } as any)}
              >
                <span
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 bg-white ${
                    customInput.questionType === 'ATTENDEE' ? 'border-sage' : 'border-edge'
                  }`}
                  aria-hidden
                >
                  {customInput.questionType === 'ATTENDEE' && (
                    <span className="h-2 w-2 rounded-full bg-sage" />
                  )}
                </span>
                <span className="text-sm text-ink-secondary">Attendee</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
}

export default CustomInputsTabContent_Events;
